import {
  AssetCategorySection,
  CategoryActionButton,
} from '@/components/features/assets/AssetCategorySection'
import { AssetSelectionBar } from '@/components/features/assets/AssetSelectionBar'
import {
  AssetSetOperationDialog,
  type AssetSetOperationIntent,
} from '@/components/features/assets/AssetSetOperationDialog'
import { CorrectCaptureTimeDialog } from '@/components/features/assets/CorrectCaptureTimeDialog'
import { QualityReport } from '@/components/features/assets/QualityReport'
import { ShareAssetSetDialog } from '@/components/features/assets/ShareAssetSetDialog'
import {
  ASSET_CATEGORIES,
  AssetCategoryKey,
  groupAssetsByCategory,
} from '@/components/features/assets/categorize'
import ImageLocationMap from '@/components/features/maps/ImageLocationMap'
import type { SelectionMode } from '@/components/features/maps/ImageLocationMap/selectionShape'
import { ProjectTabBar } from '@/components/features/projects/ProjectTabBar'
import NotFound from '@/components/utils/NotFound'
import RouteLoadFailed from '@/components/utils/RouteLoadFailed'
import { useAssetSelection } from '@/hooks/useAssetSelection'
import { useAssetSetAssets } from '@/hooks/useAssetSetAssets'
import { useAssetSetDownload } from '@/hooks/useAssetSetDownload'
import { useOdmQualityReport } from '@/hooks/useOdmQualityReport'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { countFileTypes, fileTypeByAssetId } from '@/lib/assetFileTypes'
import { sizeByAssetId, totalBytesFor } from '@/lib/assetSizes'
import { compareAssetsByCapturedAt } from '@/lib/assetSort'
import { captureDisplayDate } from '@/lib/captureTime'
import { convertSlimAssetsToPositionData } from '@/lib/geoUtils'
import { throwLoaderError } from '@/lib/loader-errors'
import { cn, formatBytes } from '@/lib/utils'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  PencilIcon,
  Square3Stack3DIcon,
  ViewfinderCircleIcon,
} from '@heroicons/react/24/outline'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import {
  AssetSetType,
  DownloadJobStatus,
  SLIM_MAX_PAGE_SIZE,
  SlimAsset,
  isEditableAssetSetType,
} from '@droidmap/asset-service-contract'
import { AccordionItem, HeroPanel } from '@droidmap/frontend-ui'
import { Card, CardContent } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import { Permission } from '@droidmap/permissions-service-contract'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/asset-sets/$assetSetId',
)({
  params: {
    parse: (params) => ({
      assetSetId: z.string().parse(params.assetSetId),
    }),
  },
  loader: async ({ params, context }) => {
    const { assetSetId, orgId, projectId } = params
    const { assetClient } = context.client
    try {
      // In parallel, and deliberately non-fatal: the summary supplies exact
      // per-category counts (droidmap#2887), which are an improvement on
      // counting what is loaded — not something worth failing the page for.
      const [assetSet, summary] = await Promise.all([
        assetClient.getAssetSet({ assetSetId }, context.metadata),
        assetClient
          .getAssetSetSummary({ assetSetId }, context.metadata)
          .catch(() => undefined),
      ])
      return {
        crumb: assetSet.name,
        // The detail page lives outside the asset-sets list route in
        // the file tree, so inject the list as a synthetic preceding
        // crumb to keep the conceptual breadcrumb chain intact.
        precedingCrumbs: [
          {
            label: 'Asset Sets',
            to: '/orgs/$orgId/projects/$projectId/asset-sets',
            params: { orgId, projectId },
          },
        ],
        assetSetId,
        assetSet,
        categoryCounts: summary?.categoryCounts,
        assetClient,
        metadata: context.metadata,
        projectId,
      }
    } catch (error) {
      // Only a 404/403 means the asset set is gone or isn't ours — a 5xx or a
      // network blip is a failed load, and reporting it as "not found" is a
      // false claim about the user's data with no way back (#2658).
      throwLoaderError(error, 'asset set')
    }
  },
  component: AssetSetDetail,
  notFoundComponent: () => <NotFound resource="Asset Set" />,
  errorComponent: () => <RouteLoadFailed resource="asset set" />,
})

const ASSET_SET_LABEL: Record<AssetSetType, string> = {
  [AssetSetType.DroneImages]: 'Drone Imagery',
  [AssetSetType.OdmOutputs]: 'ODM Outputs',
  [AssetSetType.Lidar]: 'LiDAR',
  [AssetSetType.MapLayers]: 'Map Layers',
  [AssetSetType.GCPOutputs]: 'GCP Outputs',
  [AssetSetType.Other]: 'Asset Set',
}

function IdCopy({ id }: { id: string }) {
  const [copied, setCopied] = useState(false)
  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can fail in non-secure contexts.
    }
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title="Copy ID"
      className={cn(
        'bg-bg-2 border-border text-text-dim inline-flex items-center gap-2 rounded-xs border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150',
        'hover:text-accent hover:border-accent-tint',
      )}
    >
      {copied ? (
        <CheckIcon className="h-3 w-3" />
      ) : (
        <ClipboardDocumentIcon className="h-3 w-3" />
      )}
      <span className="max-w-[18rem] truncate">{id}</span>
    </button>
  )
}

/** How the detail view's asset grid is ordered. */
type ImageSortKey = 'capturedAt' | 'name'

/** The draw tools offered above the map (droidmap#2900). */
const MAP_SELECTION_TOOLS: {
  mode: SelectionMode
  label: string
  Icon: typeof Square3Stack3DIcon
}[] = [
  { mode: 'rectangle', label: 'Box', Icon: ViewfinderCircleIcon },
  { mode: 'freeform', label: 'Lasso', Icon: PencilIcon },
]

function AssetSetDetail() {
  const {
    assetSet,
    assetSetId,
    assetClient,
    categoryCounts,
    metadata,
    projectId,
  } = Route.useLoaderData()
  const { orgId } = Route.useParams()
  const navigate = useNavigate()
  // Positions for the map, from the slim projection rather than the grid's
  // signed listing (droidmap#2771): pins need coordinates, not URLs.
  const [positionAssets, setPositionAssets] = useState<SlimAsset[]>([])
  // Whether `positionAssets` is the whole set, which is what lets "Select
  // all" skip paging the grid in.
  const [positionsComplete, setPositionsComplete] = useState(false)
  // The walk in flight, so "Select all" can wait for it rather than page the
  // grid in — after a bulk operation the walk restarts, and that is exactly
  // when a user reaches for Select all again.
  const positionsWalkRef = useRef<Promise<SlimAsset[] | null> | null>(null)
  // An ODM output set is a handful of artifacts, and the Quality Report is
  // found by name among them, so it loads whole. Paging it would buy nothing
  // and risk the report vanishing — see acceptance item 5 on droidmap#2773.
  const isOdmOutputs = assetSet?.assetSetType === AssetSetType.OdmOutputs
  const [imageSort, setImageSort] = useState<ImageSortKey>('capturedAt')
  // Bumped after a bulk operation to re-list the set's assets.
  const [assetsVersion, setAssetsVersion] = useState(0)
  const {
    assets,
    isLoading,
    isLoadingMore,
    hasError,
    hasMore,
    loadMore,
    loadAll,
  } = useAssetSetAssets({
    assetClient,
    assetSetId,
    metadata,
    loadWholeSet: isOdmOutputs,
    version: assetsVersion,
  })
  const [operationIntent, setOperationIntent] =
    useState<AssetSetOperationIntent | null>(null)
  // Remounts the operation dialog per opening, so no state leaks between runs.
  const [operationDialogKey, setOperationDialogKey] = useState(0)

  // Compare flow only supports DroneImages (image comparison) and OdmOutputs
  // (map comparison) — disable the button on every other type so we don't
  // dead-end the user on the compare page with no compatible asset sets.
  const isCompareEligible =
    assetSet?.assetSetType === AssetSetType.DroneImages ||
    assetSet?.assetSetType === AssetSetType.OdmOutputs

  const handleCompare = useCallback(() => {
    if (!isCompareEligible) return
    navigate({
      to: '/orgs/$orgId/projects/$projectId/compare',
      params: { orgId, projectId },
      search: { preselectId: assetSetId },
    })
  }, [assetSetId, isCompareEligible, navigate, orgId, projectId])

  // Open the advanced map view — the set's raster/COG assets as toggleable,
  // opacity-controllable layers with a basemap picker (the routed simple map
  // is a single-asset display view).
  const handleOpenAdvancedMap = useCallback(() => {
    navigate({
      to: '/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId/map-advanced',
      params: { orgId, projectId, assetSetId },
    })
  }, [assetSetId, navigate, orgId, projectId])

  const handleOpenAnnotator = useCallback(() => {
    navigate({
      to: '/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId/annotate',
      params: { orgId, projectId, assetSetId },
    })
  }, [assetSetId, navigate, orgId, projectId])

  const {
    initiateDownload,
    isInitiating,
    isDownloading,
    downloadJob,
    progress,
    failedAssets,
    error: downloadError,
    reset: resetDownload,
  } = useAssetSetDownload(metadata)

  // Auto-clear the spinner once the job lands a terminal state. Failed assets
  // > 0 keeps the bar visible for a beat so the user notices the warning toast
  // surfaced elsewhere; on a clean completion we reset after a short delay.
  useEffect(() => {
    if (downloadJob?.status === DownloadJobStatus.Completed && !failedAssets) {
      const t = setTimeout(() => resetDownload(), 2000)
      return () => clearTimeout(t)
    }
    return undefined
  }, [downloadJob?.status, failedAssets, resetDownload])

  const handleDownloadAll = useCallback(() => {
    if (isInitiating || isDownloading) return
    initiateDownload(assetSetId)
  }, [assetSetId, initiateDownload, isInitiating, isDownloading])

  // Share links expose the set publicly — gated on the same write permission
  // the backend enforces for share-link creation.
  const router = useRouter()
  const { hasPermission } = usePermissionCheck()
  const canShareAssetSet = hasPermission(Permission.UPDATE_ASSET_SET)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  // Correcting capture times rewrites every dated image in the set, so it is
  // gated on the same write permission as sharing.
  const [captureDialogOpen, setCaptureDialogOpen] = useState(false)

  const downloadDisabled = !assetSetId || isInitiating || isDownloading
  const downloadLabel = (() => {
    if (downloadError) return 'Retry Download'
    // The hook's "downloading" phase is server-side ZIP preparation — we
    // don't track the user's local file save. Surface it as "Preparing" so
    // the percent matches what's actually happening (assets being bundled).
    if (isInitiating || isDownloading)
      return progress
        ? `Preparing… ${Math.round(progress.percent)}%`
        : 'Preparing…'
    if (downloadJob?.status === DownloadJobStatus.Completed) return 'Downloaded'
    return 'Download All'
  })()

  // Map pins come from the slim projection: coordinates and dimensions, no
  // presigned URLs (droidmap#2771). The popup's image is fetched on click —
  // a bare redirect path cannot be used from an `<img>` here, so signing
  // every asset up front to serve a handful of popups was the old cost.
  //
  // Every positioned asset gets a pin, not only images: the map is how a
  // user selects by area, and a format stored as `other` must be reachable
  // by a box or lasso too. This map draws no footprints, so a pin without
  // dimensions costs nothing.
  const imagesWithPosition = useMemo(
    () =>
      convertSlimAssetsToPositionData(positionAssets, { anyAssetType: true }),
    [positionAssets],
  )

  useEffect(() => {
    let isMounted = true

    const loadPositions = async () => {
      try {
        // Start from empty, as the grid's loader does. A re-walk after a bulk
        // operation takes one round trip per 1,000 assets, and until it
        // finishes the old pins stay on the map — clickable and lassoable
        // (droidmap#2900). Selecting one of those now reaches the API, which
        // rejects the whole batch with "N of the selected assets are not in
        // this asset set" and names none of them.
        if (isMounted) {
          setPositionAssets([])
          setPositionsComplete(false)
        }
        const all: SlimAsset[] = []
        let next: string | undefined = undefined
        do {
          const page = await assetClient.listSlimAssetsByAssetSet(
            { assetSetId, pageSize: SLIM_MAX_PAGE_SIZE, next },
            metadata,
          )
          all.push(...(page.items ?? []))
          next = page.next
        } while (next)
        if (isMounted) {
          setPositionAssets(all)
          setPositionsComplete(true)
        }
        // A superseded walk (the set changed under it) answers nothing.
        return isMounted ? all : null
      } catch (ignoreError) {
        // The map degrades to no pins rather than failing the page; the grid
        // and header have their own error handling.
        if (isMounted) setPositionAssets([])
        return null
      }
    }

    positionsWalkRef.current = loadPositions()

    return () => {
      isMounted = false
    }
  }, [assetSetId, assetClient, metadata, assetsVersion])

  // Hooks must run unconditionally — keep groupAssetsByCategory memoised even
  // when the assetSet payload is missing (the early return below renders the
  // error card without using `groups`).
  // Order the assets before grouping — groupAssetsByCategory preserves input
  // order, so a single sort here orders every category's grid.
  const sortedAssets = useMemo(() => {
    const arr = [...assets]
    arr.sort(
      imageSort === 'capturedAt'
        ? compareAssetsByCapturedAt
        : (a, b) => a.name.localeCompare(b.name),
    )
    return arr
  }, [assets, imageSort])
  const groups = useMemo(
    () => groupAssetsByCategory(sortedAssets, isOdmOutputs),
    [sortedAssets, isOdmOutputs],
  )

  // ODM writes its processing report to `odm_report/{stats.json,report.pdf}`.
  // Surface the parsed stats as a Quality Report and link the PDF, but only
  // for ODM-output sets (other set types never carry these artifacts).
  const statsAsset = useMemo(
    () =>
      isOdmOutputs
        ? assets.find((a) => a.name.toLowerCase() === 'stats.json')
        : undefined,
    [assets, isOdmOutputs],
  )
  const reportPdfAsset = useMemo(
    () =>
      isOdmOutputs
        ? assets.find((a) => a.name.toLowerCase() === 'report.pdf')
        : undefined,
    [assets, isOdmOutputs],
  )
  const qualityReport = useOdmQualityReport(statsAsset)

  /* ---------------------------- Bulk selection ---------------------------- */

  const orderedCategories: AssetCategoryKey[] = isOdmOutputs
    ? ['map', 'model', 'data', 'documents', 'other']
    : ['imagery', 'map', 'model', 'data', 'documents', 'other']

  // Display order across every section, so a shift-click range follows what
  // the user sees rather than list order.
  const orderedAssetIds = useMemo(
    () => orderedCategories.flatMap((key) => groups[key].map((a) => a.id)),
    // `orderedCategories` is derived from `isOdmOutputs`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups, isOdmOutputs],
  )

  /**
   * The flight path: every positioned asset in capture order.
   *
   * What a shift-click range on the map runs along (droidmap#2900). Grid
   * display order is category-grouped, which projected onto a map means
   * nothing; capture order is the sequence the drone actually flew, so a
   * range between two pins is the frames between them.
   */
  const capturedOrderAssetIds = useMemo(() => {
    // Keyed off the pins the user can actually see, not every asset in the
    // set: `positionAssets` is the unfiltered slim walk, and an asset with no
    // coordinates (or no dimensions) never becomes a pin. Ranging over those
    // would sweep in assets with nothing on screen between the two clicks.
    const plotted = new Set(
      imagesWithPosition.flatMap((image) =>
        image.assetId ? [image.assetId] : [],
      ),
    )
    return [...positionAssets]
      .filter((asset) => plotted.has(asset.id))
      .sort(compareAssetsByCapturedAt)
      .map((asset) => asset.id)
  }, [positionAssets, imagesWithPosition])

  // The slim walk reaches every asset in the set, including those the grid
  // has not paged in — they stay selectable without joining the display
  // order (droidmap#2900), and "Select all" takes them without loading a
  // card for each.
  const slimAssetIds = useMemo(
    () => positionAssets.map((asset) => asset.id),
    [positionAssets],
  )
  const selection = useAssetSelection(orderedAssetIds, slimAssetIds)

  /** Selection mode for the map's draw tools; null while panning normally. */
  const [mapSelectionMode, setMapSelectionMode] =
    useState<SelectionMode | null>(null)

  const { add: addToSelection, clear: clearSelection } = selection

  const handleShapeSelect = useCallback(
    (assetIds: string[], { additive }: { additive: boolean }) => {
      // A marquee replaces unless a modifier was held when the drag started,
      // matching selection tools elsewhere.
      if (!additive) clearSelection()
      addToSelection(assetIds)
    },
    // The hook returns a fresh object each render, so depending on `selection`
    // would defeat the memo; its callbacks are individually stable.
    [addToSelection, clearSelection],
  )

  /**
   * "Select all" means the whole set, not the part of it on screen.
   *
   * The slim walk already holds every id, so selecting from it is instant
   * however large the set. Paging the grid in instead signs and renders a
   * card per asset, which on a large set stalls the page for the sake of a
   * list of ids. A walk still in flight is waited for; only a failed one
   * falls back to fetching the remaining grid pages.
   */
  const handleSelectAll = useCallback(async () => {
    if (positionsComplete || !hasMore) {
      selection.selectAll()
      return
    }
    const walked = await positionsWalkRef.current
    selection.add(walked ? walked.map((asset) => asset.id) : await loadAll())
  }, [positionsComplete, hasMore, loadAll, selection])

  const selectedAssetIds = useMemo(
    () => [...selection.selectedIds],
    [selection.selectedIds],
  )
  /**
   * Size of every asset in the set, from the slim walk (droidmap#2900).
   *
   * The grid holds only the pages it has fetched, so sizing a selection from
   * it was exact only while the selection came from the grid. A lasso can
   * pick assets with no card, and the figure that results is read out in the
   * delete confirmation — understating what is about to be destroyed is the
   * one place this must not be approximate.
   */
  const assetSizes = useMemo(
    // Slim walk first — it covers the whole set; the grid's pages fill in
    // anything it has not reached yet.
    () => sizeByAssetId(positionAssets, assets),
    [positionAssets, assets],
  )

  const selectedBytes = useMemo(
    () => totalBytesFor(selection.selectedIds, assetSizes),
    [selection.selectedIds, assetSizes],
  )

  // From both views for the same reason as the sizes: a lasso reaches assets
  // the grid has not paged in.
  const assetFileTypes = useMemo(
    () => fileTypeByAssetId(positionAssets, assets),
    [positionAssets, assets],
  )
  const { add: addIds, remove: removeIds, selectedIds } = selection
  /**
   * The Type filter's working state: the selection it started from, and the
   * types unticked since.
   *
   * Unticking drops that type from the selection and ticking it again brings
   * the same assets back, so the rows keep listing every type the selection
   * started with. Any other change to the selection (a new box, a click,
   * Clear) leaves it out of step with `pool`, and the filter starts over from
   * whatever is selected then.
   */
  const [typeFilter, setTypeFilter] = useState<{
    pool: ReadonlySet<string>
    excluded: ReadonlySet<string>
  } | null>(null)
  const activeTypeFilter = useMemo(() => {
    if (!typeFilter) return null
    const expected = [...typeFilter.pool].filter(
      (id) => !typeFilter.excluded.has(assetFileTypes.get(id) ?? ''),
    )
    const inStep =
      expected.length === selectedIds.size &&
      expected.every((id) => selectedIds.has(id))
    return inStep ? typeFilter : null
  }, [typeFilter, selectedIds, assetFileTypes])
  // Once out of step, forget it: a later selection that happened to equal
  // the expected set would otherwise revive the old unticks — Clear, then
  // re-pick the same JPEGs, and TIFF shows unticked with assets to restore
  // that the new selection never held. Reset during render, as React
  // prescribes for state derived from other state, so no stale frame
  // renders. The filter's own changes update the selection and this state
  // together, so they never land here.
  if (typeFilter && !activeTypeFilter) setTypeFilter(null)
  const selectedFileTypes = useMemo(
    () => countFileTypes(activeTypeFilter?.pool ?? selectedIds, assetFileTypes),
    [activeTypeFilter, selectedIds, assetFileTypes],
  )
  /** Include or drop one file type, e.g. untick TIFF to keep a lasso's JPEGs. */
  const handleFileTypeChange = useCallback(
    (type: string, included: boolean) => {
      const { pool, excluded } = activeTypeFilter ?? {
        pool: selectedIds,
        excluded: new Set<string>(),
      }
      const ofType = [...pool].filter(
        (id) => (assetFileTypes.get(id) ?? '') === type,
      )
      const nextExcluded = new Set(excluded)
      if (included) {
        nextExcluded.delete(type)
        addIds(ofType)
      } else {
        nextExcluded.add(type)
        removeIds(ofType)
      }
      setTypeFilter({ pool, excluded: nextExcluded })
    },
    [activeTypeFilter, selectedIds, assetFileTypes, addIds, removeIds],
  )

  // Removing assets from (or adding them to) an output set would strand its
  // manifest and its task's viewable-asset list, so those sets only copy out.
  const membershipEditable =
    !!assetSet && isEditableAssetSetType(assetSet.assetSetType)
  const canRemoveAssets =
    membershipEditable && hasPermission(Permission.DELETE_ASSET)
  const canCreateAssetSet = hasPermission(Permission.CREATE_ASSET_SET)
  const canCopyAssets = hasPermission(Permission.CREATE_ASSET)
  const selectionIntents = [
    ...(canRemoveAssets && canCreateAssetSet ? (['split'] as const) : []),
    ...(canRemoveAssets ? (['move'] as const) : []),
    ...(canCopyAssets ? (['copy'] as const) : []),
    ...(canRemoveAssets ? (['delete'] as const) : []),
  ]
  const selectionEnabled = selectionIntents.length > 0

  const openOperation = useCallback((intent: AssetSetOperationIntent) => {
    setOperationDialogKey((key) => key + 1)
    setOperationIntent(intent)
  }, [])

  const handleOperationFinished = useCallback(() => {
    selection.clear()
    setAssetsVersion((version) => version + 1)
    // The loader's set carries the counters and capture date that just moved.
    router.invalidate()
  }, [router, selection])

  // Escape clears the selection, unless a dialog has the keyboard.
  useEffect(() => {
    if (selection.count === 0 || operationIntent) return undefined
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') selection.clear()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [operationIntent, selection])

  if (!assetSet?.id) {
    return (
      <div className="p-4">
        <Card className="bg-danger">
          <CardContent className="pt-6">
            <div className="text-btn-fg flex items-center space-x-2">
              <ExclamationCircleIcon className="h-5 w-5" />
              <p>Error: Failed to load asset set data</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // From the set's own counters (droidmap#2767), so the header is right
  // immediately and stays right when the grid holds only the first page.
  const assetCount = assetSet.assetCount ?? assets.length
  const totalBytes = Object.values(assetSet.storageSummary).reduce(
    (sum, bytes) => sum + bytes,
    0,
  )
  const eyebrowLabel = `Asset Set · ${ASSET_SET_LABEL[assetSet.assetSetType] ?? 'Asset Set'}`
  const processedDate = assetSet.created
    ? new Date(assetSet.created).toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null
  // The flight date, in the camera's clock rather than the viewer's — sitting
  // next to an upload date that is a real instant and *is* localised. The two
  // are different kinds of value: "processed" happened on our clock, "captured"
  // happened on the photographer's.
  const capturedDate = captureDisplayDate(
    assetSet.capturedAt,
    assetSet.capturedAtOffsetMinutes,
  )?.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })

  // Per-category meta blurb shown in the section header.
  const categoryMeta: Partial<Record<AssetCategoryKey, string>> = {
    model: groups.model.length > 0 ? 'Textured mesh' : undefined,
    data: groups.data.length > 0 ? 'Point clouds & metadata' : undefined,
  }

  return (
    <div>
      <HeroPanel
        className="mb-7"
        eyebrow={
          <span className="inline-flex items-center gap-2.5">
            <span
              aria-hidden
              className="bg-accent inline-block h-1.5 w-1.5 rounded-full shadow-[0_0_10px_currentColor] animate-pulse"
            />
            {eyebrowLabel}
          </span>
        }
        title={assetSet.name || 'Asset Set'}
        meta={
          <>
            <IdCopy id={assetSet.id} />
            {capturedDate && (
              <>
                <span className="text-text-mute">·</span>
                <span>
                  Captured{' '}
                  <strong className="text-text font-medium">
                    {capturedDate}
                  </strong>
                </span>
              </>
            )}
            {processedDate && (
              <>
                <span className="text-text-mute">·</span>
                <span>
                  Processed{' '}
                  <strong className="text-text font-medium">
                    {processedDate}
                  </strong>
                </span>
              </>
            )}
          </>
        }
        actions={
          <div className="flex flex-col items-end gap-3 pr-1">
            <div className="flex items-end gap-9">
              <div className="text-right">
                <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                  {assetCount.toLocaleString()}
                </div>
                <div className="text-micro text-text-mute mt-2">Assets</div>
              </div>
              <div className="text-right">
                <div className="text-text font-mono text-[26px] font-medium leading-none tracking-tight">
                  {formatBytes(totalBytes, 2)}
                </div>
                <div className="text-micro text-text-mute mt-2">Size</div>
              </div>
            </div>
            {/* Action buttons sit at the bottom of the right column so
                they align horizontally with the meta row on the left
                — no empty gap below the metadata. */}
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!isCompareEligible}
                onClick={handleCompare}
                title={
                  isCompareEligible
                    ? 'Compare with another asset set'
                    : 'Comparison only supports drone imagery or ODM outputs'
                }
              >
                <ArrowsRightLeftIcon className="h-4 w-4" />
                Compare
              </Button>
              {canShareAssetSet && assetSet?.capturedAt && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCaptureDialogOpen(true)}
                  title="Correct the capture time for every image in this set"
                >
                  <ClockIcon className="h-4 w-4" />
                  Capture time
                </Button>
              )}
              {canCreateAssetSet && canCopyAssets && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={assetCount === 0 || isLoading}
                  onClick={() => openOperation('clone')}
                  title="Copy every asset into a new asset set"
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  Clone
                </Button>
              )}
              {canShareAssetSet && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShareDialogOpen(true)}
                  title="Create a public download link"
                >
                  <LinkIcon className="h-4 w-4" />
                  Share
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                disabled={downloadDisabled}
                onClick={handleDownloadAll}
              >
                {isInitiating || isDownloading ? (
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDownTrayIcon className="h-4 w-4" />
                )}
                {downloadLabel}
              </Button>
            </div>
            {downloadError && (
              <p className="text-danger font-mono text-[11px]">
                {downloadError.message || 'Download failed.'}
              </p>
            )}
          </div>
        }
      />

      {assetSet && (
        <ShareAssetSetDialog
          assetSet={assetSet}
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          metadata={metadata}
        />
      )}

      {assetSet && (
        <CorrectCaptureTimeDialog
          assetSet={assetSet}
          isOpen={captureDialogOpen}
          onClose={() => setCaptureDialogOpen(false)}
          metadata={metadata}
          // The correction rewrites capture times across the set, so the
          // loader's snapshot of the set and its assets is stale afterwards.
          onCorrected={() => router.invalidate()}
        />
      )}

      {assetSet && operationIntent && (
        <AssetSetOperationDialog
          key={operationDialogKey}
          intent={operationIntent}
          assetSet={assetSet}
          assetIds={selectedAssetIds}
          selectedBytes={selectedBytes}
          metadata={metadata}
          isOpen
          onClose={() => setOperationIntent(null)}
          onFinished={handleOperationFinished}
          onOpenAssetSet={(targetId) => {
            setOperationIntent(null)
            navigate({
              to: '/orgs/$orgId/projects/$projectId/asset-sets/$assetSetId',
              params: { orgId, projectId, assetSetId: targetId },
            })
          }}
        />
      )}

      <ProjectTabBar orgId={orgId} projectId={projectId} />

      {/* ODM processing quality report — only when the set carries a
          stats.json. Renders once parsed; a fetch/parse failure silently
          omits the section since the raw report.pdf/stats.json stay
          downloadable in the asset grid below. */}
      {statsAsset && (
        <section className="mb-10">
          <AccordionItem
            variant="panel"
            eyebrow="Processing quality"
            title="Quality Report"
            icon={<ClipboardDocumentCheckIcon />}
          >
            {qualityReport.isLoading ? (
              <div className="flex justify-center p-6">
                <Spinner size="md" className="text-accent" />
              </div>
            ) : qualityReport.report ? (
              <QualityReport
                report={qualityReport.report}
                reportPdfAsset={reportPdfAsset}
              />
            ) : null}
          </AccordionItem>
        </section>
      )}

      {/* Asset-loading error banner */}
      {hasError && (
        <Card className="bg-danger mb-6">
          <CardContent className="pt-6">
            <div className="text-btn-fg flex items-center space-x-2">
              <ExclamationCircleIcon className="h-5 w-5" />
              <p>Error: Failed to load assets</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading state — nothing has arrived yet */}
      {isLoading && assets.length === 0 && !hasError && (
        <div className="flex justify-center p-8">
          <Spinner size="md" className="text-accent" />
        </div>
      )}

      {/* Asset-locations map for asset sets that have GPS-tagged imagery. */}
      {imagesWithPosition.length > 0 && (
        <section className="mb-10">
          <header className="border-border mb-4 flex items-baseline justify-between gap-3 border-b pb-2.5">
            <h2 className="text-text inline-flex items-center gap-3 text-[15px] font-semibold">
              <span className="bg-accent-soft text-accent grid h-7 w-7 place-items-center rounded-sm">
                <Square3Stack3DIcon className="h-3.5 w-3.5" />
              </span>
              Asset Locations
              <span className="text-text-mute font-mono text-[11px] tracking-[0.04em]">
                {imagesWithPosition.length} OF {assetCount} GEOTAGGED
                {isLoading ? ' · LOADING…' : ''}
              </span>
            </h2>
            {selectionEnabled && (
              <div className="flex items-center gap-1">
                {MAP_SELECTION_TOOLS.map(({ mode, label, Icon }) => (
                  <Button
                    key={mode}
                    variant={mapSelectionMode === mode ? 'primary' : 'ghost'}
                    size="sm"
                    aria-pressed={mapSelectionMode === mode}
                    title={`${label} select — hold Ctrl to add to the selection`}
                    onClick={() =>
                      setMapSelectionMode((prev) =>
                        prev === mode ? null : mode,
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </Button>
                ))}
              </div>
            )}
          </header>
          {mapSelectionMode && (
            <p className="text-text-mute mb-2 text-[11px]">
              Drag to select the pins inside the shape, or click one to pick it
              out. Hold Ctrl to add to the current selection instead of
              replacing it. Scroll to zoom and drag with the middle button to
              pan.
            </p>
          )}
          <ImageLocationMap
            imagesWithPosition={imagesWithPosition}
            className="border-border rounded-lg border"
            selectedImageIds={selectedAssetIds}
            selectionMode={selectionEnabled ? mapSelectionMode : null}
            onShapeSelect={selectionEnabled ? handleShapeSelect : undefined}
            // A popup over the pins would fight the draw tools for the same
            // clicks, so it is the plain map that pops up and the selecting
            // map that does not.
            showPopups={!mapSelectionMode}
            // Clicking a pin selects it, with the grid's modifiers: plain
            // click flips one, Shift extends a range along the flight path.
            //
            // Only while a draw tool is active. With no tool the map is an
            // ordinary map — click a pin and it tells you about the photo,
            // exactly as it did before any of this (droidmap#2900).
            onImageSelect={
              selectionEnabled && mapSelectionMode
                ? (image, modifiers) =>
                    image.assetId &&
                    selection.toggle(image.assetId, {
                      range: modifiers.range,
                      order: capturedOrderAssetIds,
                    })
                : undefined
            }
          />
        </section>
      )}

      {/* Asset ordering control — sorts every category's grid. */}
      {assets.length > 0 && (
        <div className="mb-5 flex items-center justify-end gap-2">
          <span className="text-text-mute font-mono text-[10px] uppercase tracking-[0.16em]">
            Sort
          </span>
          <div className="border-border bg-bg-2 inline-flex overflow-hidden rounded-md border">
            {(
              [
                { key: 'capturedAt', label: 'Capture date' },
                { key: 'name', label: 'Name' },
              ] as const
            ).map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setImageSort(key)}
                className={cn(
                  'px-3 py-1.5 font-mono text-[11px] transition-colors duration-150',
                  imageSort === key
                    ? 'bg-accent-soft text-accent'
                    : 'text-text-dim hover:text-text',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Categorised asset grid */}
      {orderedCategories.map((key) => {
        const def = ASSET_CATEGORIES[key]
        return (
          <AssetCategorySection
            key={key}
            category={key}
            title={def.label}
            assets={groups[key]}
            totalCount={categoryCounts?.[key]}
            large={key === 'map'}
            meta={categoryMeta[key]}
            action={
              key === 'map' ? (
                <CategoryActionButton onClick={handleOpenAdvancedMap}>
                  <Square3Stack3DIcon className="h-4 w-4" />
                  Advanced view
                </CategoryActionButton>
              ) : key === 'imagery' && !isOdmOutputs ? (
                <CategoryActionButton onClick={handleOpenAnnotator}>
                  <ViewfinderCircleIcon className="h-4 w-4" />
                  Label for ML
                </CategoryActionButton>
              ) : undefined
            }
            projectId={projectId}
            metadata={metadata}
            assetSetKind={isOdmOutputs ? 'odm' : 'imagery'}
            assetSetName={assetSet.name}
            selection={
              selectionEnabled
                ? {
                    isSelected: selection.isSelected,
                    active: selection.count > 0,
                    onToggle: (asset, { range }) =>
                      selection.toggle(asset.id, { range }),
                  }
                : undefined
            }
          />
        )
      })}

      {hasMore && (
        <div className="flex justify-center p-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void loadMore()}
            disabled={isLoadingMore}
          >
            {isLoadingMore
              ? 'Loading…'
              : `Load more (${assets.length.toLocaleString()} of ${assetCount.toLocaleString()})`}
          </Button>
        </div>
      )}

      <AssetSelectionBar
        count={selection.count}
        totalCount={assetCount}
        selectedBytes={selectedBytes}
        intents={selectionIntents}
        onAction={openOperation}
        onSelectAll={() => void handleSelectAll()}
        onClear={selection.clear}
        fileTypes={selectedFileTypes}
        excludedFileTypes={activeTypeFilter?.excluded}
        onFileTypeChange={handleFileTypeChange}
      />

      {/* Trailing loading indicator while more pages stream in. */}
      {isLoading && assets.length > 0 && (
        <div className="flex justify-center p-4">
          <div className="flex items-center space-x-2">
            <Spinner size="sm" className="text-accent" />
            <span className="text-text-dim text-sm">Loading more assets…</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && assetCount === 0 && !hasError && (
        <Card className="bg-panel-2">
          <CardContent className="pt-6">
            <p className="text-text-dim">No assets found in this asset set.</p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
